import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';
import { LoadingSpinner } from '../components';

const AddressesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ label: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', address_type: 'home' });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/customer/login'); return; }
    loadAddresses();
  }, [isAuthenticated]);

  const loadAddresses = async () => {
    setIsLoading(true);
    try {
      const res = await apiService.getAddresses();
      const list = res?.data?.addresses || res?.data || [];
      setAddresses(Array.isArray(list) ? list : []);
    } catch (e) {}
    setIsLoading(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let res;
      if (editingId) {
        res = await apiService.updateAddress(editingId, formData);
      } else {
        res = await apiService.createAddress(formData);
      }
      if (res?.success !== false) {
        setShowForm(false);
        setEditingId(null);
        setFormData({ label: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', address_type: 'home' });
        loadAddresses();
      } else {
        alert(res?.error || 'Failed to save address');
      }
    } catch (e) {
      alert('Network error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    await apiService.deleteAddress(id);
    setAddresses(prev => prev.filter(a => a.id !== id));
  };

  const handleSetDefault = async (id) => {
    await apiService.setDefaultAddress(id);
    setAddresses(prev => prev.map(a => ({ ...a, is_default: a.id === id })));
  };

  const handleEdit = (addr) => {
    setEditingId(addr.id);
    setFormData({
      label: addr.label || '',
      address_line1: addr.address_line1 || addr.street || '',
      address_line2: addr.address_line2 || '',
      city: addr.city || '',
      state: addr.state || '',
      zip_code: addr.zip_code || addr.zip || '',
      address_type: addr.address_type || 'home',
    });
    setShowForm(true);
  };

  const addressTypeIcons = { home: '🏠', work: '🏢', other: '📍' };

  return (
    <div className="addresses-page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/customer/profile')}>← Back</button>
        <h2 className="page-title">📍 Saved Addresses</h2>
        <button className="btn-primary" onClick={() => { setShowForm(true); setEditingId(null); setFormData({ label: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', address_type: 'home' }); }}>
          + Add
        </button>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="addresses-list">
          {addresses.length === 0 && !showForm && (
            <div className="empty-state">
              <div className="empty-icon">📍</div>
              <h3>No addresses saved</h3>
              <button className="btn-primary" onClick={() => setShowForm(true)}>Add Address</button>
            </div>
          )}
          {addresses.map(addr => (
            <div key={addr.id} className={`address-card ${addr.is_default ? 'default' : ''}`}>
              <div className="address-card-header">
                <span className="address-type-icon">{addressTypeIcons[addr.address_type] || '📍'}</span>
                <div className="address-info">
                  <span className="address-label">{addr.label || addr.address_type || 'Address'}</span>
                  {addr.is_default && <span className="default-badge">Default</span>}
                </div>
              </div>
              <p className="address-text">
                {[addr.address_line1, addr.address_line2, addr.city, addr.state, addr.zip_code].filter(Boolean).join(', ')}
              </p>
              <div className="address-actions">
                {!addr.is_default && (
                  <button className="link-btn" onClick={() => handleSetDefault(addr.id)}>Set Default</button>
                )}
                <button className="link-btn" onClick={() => handleEdit(addr)}>Edit</button>
                <button className="link-btn danger" onClick={() => handleDelete(addr.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Address Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-container modal-medium" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? 'Edit Address' : 'Add Address'}</h3>
              <button className="modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Type</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['home', 'work', 'other'].map(t => (
                    <button
                      key={t}
                      className={`type-chip ${formData.address_type === t ? 'selected' : ''}`}
                      onClick={() => setFormData(d => ({ ...d, address_type: t }))}
                    >
                      {addressTypeIcons[t]} {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Label (e.g. "Home", "Office")</label>
                <input type="text" className="form-input" value={formData.label} onChange={e => setFormData(d => ({ ...d, label: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Street Address *</label>
                <input type="text" className="form-input" value={formData.address_line1} onChange={e => setFormData(d => ({ ...d, address_line1: e.target.value }))} placeholder="123 Main St" />
              </div>
              <div className="form-group">
                <label className="form-label">Apt/Suite/Unit</label>
                <input type="text" className="form-input" value={formData.address_line2} onChange={e => setFormData(d => ({ ...d, address_line2: e.target.value }))} placeholder="Apt 4B" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">City *</label>
                  <input type="text" className="form-input" value={formData.city} onChange={e => setFormData(d => ({ ...d, city: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <input type="text" className="form-input" value={formData.state} onChange={e => setFormData(d => ({ ...d, state: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">ZIP Code</label>
                <input type="text" className="form-input" value={formData.zip_code} onChange={e => setFormData(d => ({ ...d, zip_code: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                <button className="btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Address'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressesPage;
